import { existsSync } from "node:fs";
import { join } from "node:path";
import type { AgentTool } from "@elyracode/agent-core";
import { Text } from "@elyracode/tui";
import { mkdir as fsMkdir, writeFile as fsWriteFile } from "fs/promises";
import { type Static, Type } from "typebox";
import type { ExtensionContext, ToolDefinition } from "../extensions/types.js";
import { wrapToolDefinition } from "./tool-definition-wrapper.js";

const MAX_NAME_LENGTH = 64;
const MAX_DESCRIPTION_LENGTH = 1024;

const extensionWriteSchema = Type.Object({
	name: Type.String({
		description:
			"Extension name: lowercase letters, digits, and hyphens only (e.g. 'jira-tools'). Becomes the file name and must be unique. Max 64 chars.",
	}),
	description: Type.String({
		description: "One-sentence description of what capability this extension adds. Max 1024 chars.",
	}),
	code: Type.String({
		description:
			'The full TypeScript source for the extension. It MUST have a default export function that receives the ExtensionAPI and registers one or more tools via elyra.registerTool(...). Import the API type with: import type { ExtensionAPI } from "@elyracode/coding-agent"; and the schema builder with: import { Type } from "typebox";',
	}),
	scope: Type.Optional(
		Type.Union([Type.Literal("user"), Type.Literal("project")], {
			description:
				"'user' saves to your global extensions (available everywhere). 'project' saves into the project (committed to git, shared with the team). Default: user.",
		}),
	),
});

export type ExtensionWriteToolInput = Static<typeof extensionWriteSchema>;

export interface ExtensionWriteToolDetails {
	name: string;
	scope: "user" | "project";
	path: string | null;
	saved: boolean;
	rejected?: boolean;
}

/** Pluggable operations for the extension-write tool (overridable for tests). */
export interface ExtensionWriteOperations {
	writeFile: (absolutePath: string, content: string) => Promise<void>;
	mkdir: (dir: string) => Promise<void>;
	exists: (path: string) => boolean;
}

const defaultExtensionWriteOperations: ExtensionWriteOperations = {
	writeFile: (path, content) => fsWriteFile(path, content, "utf-8"),
	mkdir: (dir) => fsMkdir(dir, { recursive: true }).then(() => {}),
	exists: (path) => existsSync(path),
};

export interface ExtensionWriteToolOptions {
	/** Directory for user-scoped extensions (e.g. ~/.elyra/agent/extensions). */
	userExtensionsDir: string;
	/** Directory for project-scoped extensions (e.g. <cwd>/.elyra/extensions). */
	projectExtensionsDir: string;
	/** Whether auto-extensions is enabled. Read fresh per call so toggles take effect immediately. */
	getAutoExtensions: () => boolean;
	/** Custom operations (default: local filesystem). */
	operations?: ExtensionWriteOperations;
}

/** Validate an extension name. Returns an error message, or null if valid. */
export function validateExtensionName(name: string): string | null {
	if (!name) return "name is required";
	if (name.length > MAX_NAME_LENGTH) {
		return `name exceeds ${MAX_NAME_LENGTH} characters (${name.length})`;
	}
	if (!/^[a-z0-9-]+$/.test(name)) {
		return "name must contain only lowercase letters, digits, and hyphens";
	}
	if (name.startsWith("-") || name.endsWith("-")) {
		return "name must not start or end with a hyphen";
	}
	return null;
}

/** Validate an extension description. Returns an error message, or null if valid. */
export function validateExtensionDescription(description: string): string | null {
	if (!description?.trim()) return "description is required";
	if (description.length > MAX_DESCRIPTION_LENGTH) {
		return `description exceeds ${MAX_DESCRIPTION_LENGTH} characters (${description.length})`;
	}
	return null;
}

/**
 * Validate that the code looks like a loadable extension: it must have a
 * default export. Returns an error message, or null if valid.
 */
export function validateExtensionCode(code: string): string | null {
	if (!code?.trim()) return "code is required";
	// Accept either `export default function` / `export default (` or a separate
	// `export default name;` statement.
	const hasDefaultExport = /export\s+default\b/.test(code);
	if (!hasDefaultExport) {
		return "extension code must have a default export (e.g. `export default function (elyra) { ... }`)";
	}
	if (!/registerTool\s*\(/.test(code)) {
		return "extension code should register at least one tool via elyra.registerTool(...)";
	}
	return null;
}

/**
 * Ask the user to approve saving a proposed extension.
 * Returns true to save. Falls back to true when no UI is present.
 */
async function confirmSave(
	ctx: ExtensionContext | undefined,
	name: string,
	scope: "user" | "project",
	description: string,
): Promise<boolean> {
	if (!ctx?.hasUI) return true;

	const result = await ctx.ui.custom<boolean>((tui, theme, _kb, done) => {
		let index = 0; // 0 = Save, 1 = Discard
		const options = ["Save extension", "Discard"];
		let cachedLines: string[] | undefined;

		function refresh() {
			cachedLines = undefined;
			tui.requestRender();
		}

		function handleInput(data: string) {
			if (data === "\x1b[A" || data === "\x1b[B") {
				index = index === 0 ? 1 : 0;
				refresh();
				return;
			}
			if (data === "\r" || data === "\n") {
				done(index === 0);
				return;
			}
			if (data === "\x1b") done(false);
		}

		function render(width: number): string[] {
			if (cachedLines) return cachedLines;
			const lines: string[] = [];
			const rule = "\u2500".repeat(width);
			lines.push(theme.fg("accent", rule));
			lines.push(theme.fg("text", " Elyra wants to add a new tool. Save it as an extension?"));
			lines.push("");
			lines.push(` ${theme.fg("accent", `${name}.ts`)} ${theme.fg("muted", `(${scope})`)}`);
			lines.push(` ${theme.fg("muted", description)}`);
			lines.push("");
			lines.push(theme.fg("warning", " Review the code before approving — extensions run with full access."));
			lines.push("");
			for (let i = 0; i < options.length; i++) {
				const selected = i === index;
				const prefix = selected ? theme.fg("accent", "> ") : "  ";
				const label = selected ? theme.fg("accent", options[i]) : theme.fg("text", options[i]);
				lines.push(prefix + label);
			}
			lines.push("");
			lines.push(theme.fg("dim", " \u2191\u2193 navigate \u2022 Enter to confirm \u2022 Esc to discard"));
			lines.push(theme.fg("accent", rule));
			cachedLines = lines;
			return lines;
		}

		return {
			render,
			invalidate: () => {
				cachedLines = undefined;
			},
			handleInput,
		};
	});

	return result === true;
}

export function createExtensionWriteToolDefinition(
	options: ExtensionWriteToolOptions,
): ToolDefinition<typeof extensionWriteSchema, ExtensionWriteToolDetails | undefined> {
	const ops = options.operations ?? defaultExtensionWriteOperations;

	return {
		name: "extension_write",
		label: "extension write",
		description:
			"Create a new Elyra extension to give yourself a tool you don't currently have. Use this when you repeatedly need a capability that no existing tool provides — for example a thin wrapper around a project-specific CLI, an API client, or a recurring multi-step operation. The extension is a TypeScript file with a default export that registers tools via elyra.registerTool(). It becomes available in the next session. Do NOT use this for one-off shell commands (use bash) or for documenting knowledge (use skill_write).",
		promptSnippet: "Create a new extension to add a tool you don't have",
		promptGuidelines: [
			"Only write an extension when a missing capability is clearly recurring. For one-off needs, use bash.",
			"Keep the extension small and focused: import { Type } from 'typebox' for the schema, export a default function that calls elyra.registerTool(...).",
			"Prefer 'project' scope for project-specific tooling, 'user' scope for general-purpose tools.",
		],
		parameters: extensionWriteSchema,
		async execute(_toolCallId, params, signal, _onUpdate, ctx) {
			const { name, description, code } = params;
			const scope: "user" | "project" = params.scope === "project" ? "project" : "user";
			const baseDetails: ExtensionWriteToolDetails = { name, scope, path: null, saved: false };

			const nameError = validateExtensionName(name);
			if (nameError) throw new Error(`Invalid extension name: ${nameError}`);

			const descError = validateExtensionDescription(description);
			if (descError) throw new Error(`Invalid extension description: ${descError}`);

			const codeError = validateExtensionCode(code);
			if (codeError) throw new Error(`Invalid extension code: ${codeError}`);

			const root = scope === "project" ? options.projectExtensionsDir : options.userExtensionsDir;
			const extPath = join(root, `${name}.ts`);

			if (ops.exists(extPath)) {
				throw new Error(
					`An extension named "${name}" already exists at ${extPath}. Choose a different name or edit the existing file directly.`,
				);
			}

			if (signal?.aborted) throw new Error("Operation aborted.");

			if (!options.getAutoExtensions()) {
				const approved = await confirmSave(ctx, name, scope, description);
				if (!approved) {
					return {
						content: [{ type: "text", text: `Extension "${name}" was not saved (user declined).` }],
						details: { ...baseDetails, rejected: true },
					};
				}
			}

			if (signal?.aborted) throw new Error("Operation aborted.");

			const header = `// ${description}\n// Auto-generated by Elyra. Review before trusting.\n\n`;
			const body = code.replace(/\s+$/, "");
			await ops.mkdir(root);
			await ops.writeFile(extPath, `${header}${body}\n`);

			return {
				content: [
					{
						type: "text",
						text: `Saved extension "${name}" (${scope}) to ${extPath}. Run /reload or restart to activate it.`,
					},
				],
				details: { ...baseDetails, path: extPath, saved: true },
			};
		},
		renderCall(args, theme, context) {
			const renderArgs = args as { name?: string; scope?: string } | undefined;
			const name = typeof renderArgs?.name === "string" ? renderArgs.name : "...";
			const scope = renderArgs?.scope === "project" ? "project" : "user";
			const text =
				`${theme.fg("toolTitle", theme.bold("extension write"))} ${theme.fg("accent", `${name}.ts`)} ` +
				theme.fg("muted", `(${scope})`);
			const component = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			component.setText(text);
			return component;
		},
		renderResult(result, _options, theme, context) {
			const details = result.details as ExtensionWriteToolDetails | undefined;
			const text = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			if (details?.rejected) {
				text.setText(theme.fg("warning", "Discarded"));
			} else if (details?.saved) {
				text.setText(theme.fg("success", "\u2713 Saved \u2022 run /reload to activate"));
			} else if (context.isError) {
				const output = result.content
					.filter((c) => c.type === "text")
					.map((c) => ("text" in c ? c.text : ""))
					.join("\n");
				text.setText(theme.fg("error", output));
			} else {
				text.setText("");
			}
			return text;
		},
	};
}

export function createExtensionWriteTool(options: ExtensionWriteToolOptions): AgentTool<typeof extensionWriteSchema> {
	return wrapToolDefinition(createExtensionWriteToolDefinition(options));
}
